import { httpsCallable } from "firebase/functions";
import { auth, functions } from "./firebase";

type ErrorSource = "window" | "unhandled-rejection" | "react-boundary" | "service-worker";

const reportError = httpsCallable(functions, "reportClientError");
const emailPattern = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
const tokenPattern = /\b[A-Za-z0-9_-]{28,}\b/g;

function scrub(value: unknown, maxLength: number) {
  return String(value ?? "")
    .replace(emailPattern, "[email]")
    .replace(tokenPattern, "[token]")
    .slice(0, maxLength);
}

function normalizeError(error: unknown) {
  if (error instanceof Error) {
    return {
      name: scrub(error.name, 80),
      message: scrub(error.message, 500),
      stack: scrub(error.stack, 3000)
    };
  }

  return {
    name: "UnknownError",
    message: scrub(error, 500),
    stack: ""
  };
}

export function reportClientError(error: unknown, source: ErrorSource) {
  const normalized = normalizeError(error);

  if (import.meta.env.DEV) {
    console.error(`[${source}]`, error);
  }

  if (!auth.currentUser) return;

  void reportError({
    ...normalized,
    source,
    path: window.location.pathname.slice(0, 300),
    appEnvironment: import.meta.env.VITE_APP_ENV || (import.meta.env.DEV ? "local" : "production")
  }).catch(() => {
    // Error reporting must never create another visible application failure.
  });
}

export function installGlobalErrorReporting() {
  window.addEventListener("error", (event) => {
    reportClientError(event.error || event.message, "window");
  });

  window.addEventListener("unhandledrejection", (event) => {
    reportClientError(event.reason, "unhandled-rejection");
  });
}
